import React, { useEffect, useMemo, useState } from "react";
import { formatRankValue, rankSchools, withMiles, withStageRanks } from "./rankSchools";
import { catchmentStanding, ofstedSortValue } from "./catchmentStanding";
import CouncilSelect from "./CouncilSelect";
import InfoTip from "../components/InfoTip";
import "./ranking.css";

const GCSE_COLUMNS = [
  {
    key: "gcseRank",
    label: "Rank",
    rankOf: "GCSE",
    group: "gcse",
    digits: 0,
  },
  {
    key: "attainment8",
    label: "Attainment 8",
    group: "gcse",
    digits: 1,
  },
  {
    key: "grade9",
    label: "Grade 9",
    group: "gcse",
    digits: 1,
    suffix: "%",
  },
  {
    key: "grade89",
    label: "Grade 8–9",
    group: "gcse",
    digits: 1,
    suffix: "%",
  },
];

const ALEVEL_COLUMNS = [
  {
    key: "alevelRank",
    label: "Rank",
    rankOf: "A level",
    group: "alevel",
    digits: 0,
  },
  {
    key: "alevelPoints",
    label: "Points",
    group: "alevel",
    digits: 1,
  },
  {
    key: "alevelAstar",
    label: "A*",
    group: "alevel",
    digits: 1,
    suffix: "%",
  },
  {
    key: "alevelAstarA",
    label: "A*–A",
    group: "alevel",
    digits: 1,
    suffix: "%",
  },
];

const AFTER_COLUMNS = [
  {
    key: "university",
    label: "University",
    group: "after",
    digits: 1,
    suffix: "%",
  },
  {
    key: "oxbridge",
    label: "Oxbridge",
    group: "after",
    digits: 0,
  },
];

const PLACE_COLUMNS = [
  {
    key: "catchmentSort",
    display: "catchment",
    label: "Status",
    sortName: "catchment",
    group: "home",
  },
  {
    key: "ofstedSort",
    display: "ofsted",
    label: "Grade",
    sortName: "Ofsted",
    group: "ofsted",
  },
];

const MILES_COLUMN = {
  key: "miles",
  label: "Miles",
  group: "miles",
  digits: 1,
};

const GROUP_LABEL = {
  gcse: "GCSE",
  alevel: "A level",
  after: "Leavers",
  home: "Catchment",
  ofsted: "Ofsted",
  miles: "Distance",
};

const GROUP_TIPS = {
  gcse:
    "GCSE results from the Department for Education. Rank is by Attainment 8 among the schools in this table. Grade shares are of counted GCSE entries.",
  alevel:
    "A level results from the Department for Education. Rank is by average points per entry among the schools in this table. A* and A*–A are shares of counted A level entries.",
};

function SortMark({ active, dir }) {
  return (
    <span
      className={`rk-sort-mark${active ? " is-on" : ""}${active && dir < 0 ? " is-desc" : ""}${active && dir > 0 ? " is-asc" : ""}`}
      aria-hidden
    >
      <svg width="10" height="12" viewBox="0 0 10 12" fill="none">
        <path className="rk-sort-mark__up" d="M5 1.5 8.5 5H1.5L5 1.5Z" fill="currentColor" />
        <path className="rk-sort-mark__down" d="M5 10.5 1.5 7h7L5 10.5Z" fill="currentColor" />
      </svg>
    </span>
  );
}

const STAGE_OPTIONS = [
  { id: "gcse", label: "GCSE" },
  { id: "alevel", label: "A level" },
];

const GENDER_OPTIONS = [
  { id: "Girls", label: "Girls" },
  { id: "Boys", label: "Boys" },
  { id: "Mixed", label: "Mixed" },
];

function yearLabel(year) {
  if (!year) return null;
  const end = Number(year);
  if (!Number.isFinite(end)) return null;
  return `${end - 1}/${String(end).slice(2)}`;
}

const CATCHMENT_OPTIONS = [
  { id: "in", label: "In catchment" },
  { id: "open", label: "Open" },
  { id: "possible", label: "Possible" },
  { id: "out", label: "Outside" },
];

function stageView(selected) {
  const gcse = selected.includes("gcse");
  const alevel = selected.includes("alevel");
  if (selected.length === 0 || (gcse && alevel)) return "both";
  return alevel ? "alevel" : "gcse";
}

function toggleChoice(selected, id) {
  if (id === "All") return [];
  return selected.includes(id)
    ? selected.filter((item) => item !== id)
    : [...selected, id];
}

function FilterGroup({ label, options, selected, onToggle }) {
  const allOn = selected.length === 0;
  return (
    <div className="rk-seg" role="group" aria-label={label}>
      <button
        type="button"
        className={allOn ? "is-on" : ""}
        aria-pressed={allOn}
        onClick={() => onToggle("All")}
      >
        All
      </button>
      {options.map((item) => {
        const on = selected.includes(item.id);
        return (
          <button
            key={item.id}
            type="button"
            className={on ? "is-on" : ""}
            aria-pressed={on}
            onClick={() => onToggle(item.id)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function columnsFor(view) {
  const results =
    view === "gcse"
      ? GCSE_COLUMNS
      : view === "alevel"
        ? ALEVEL_COLUMNS
        : [...GCSE_COLUMNS, ...ALEVEL_COLUMNS, ...AFTER_COLUMNS];
  return [...results, ...PLACE_COLUMNS, MILES_COLUMN];
}

function defaultSort(view) {
  if (view === "alevel") return { key: "alevelPoints", dir: -1 };
  return { key: "attainment8", dir: -1 };
}

/** Stage bands that get a divider on their right edge (before the next group). */
const SPLIT_AFTER_GROUPS = new Set(["gcse", "alevel", "after"]);

function columnGroups(columns) {
  const groups = [];
  columns.forEach((column) => {
    const last = groups[groups.length - 1];
    if (last && last.id === column.group) last.count += 1;
    else groups.push({ id: column.group, label: GROUP_LABEL[column.group], count: 1 });
  });
  return groups;
}

function previousGroupId(columns, groupId) {
  const order = [];
  for (const column of columns) {
    if (order[order.length - 1] !== column.group) order.push(column.group);
  }
  const index = order.indexOf(groupId);
  return index > 0 ? order[index - 1] : null;
}

function groupSplitLeft(groupId, columns) {
  const prev = previousGroupId(columns, groupId);
  return prev != null && SPLIT_AFTER_GROUPS.has(prev);
}

function bandClass(column, columns) {
  const same = columns.filter((item) => item.group === column.group);
  const first = same[0]?.key === column.key;
  const last = same[same.length - 1]?.key === column.key;
  const splitLeft = first && groupSplitLeft(column.group, columns);
  return [
    "rk-num",
    `rk-band rk-band--${column.group}`,
    first ? "rk-band--start" : "",
    last ? "rk-band--end" : "",
    splitLeft ? "rk-split-left" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export default function RankingPage({
  home,
  onOpenSchool,
  directory,
  catchmentCheckBySchoolId,
  catchmentReady = false,
}) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stages, setStages] = useState([]);
  const [genders, setGenders] = useState([]);
  const [catchments, setCatchments] = useState([]);
  const [councilsOn, setCouncilsOn] = useState([]);
  const [nearMe, setNearMe] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState(defaultSort("both"));

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch("/api/ranking")
      .then((res) => {
        if (!res.ok) throw new Error("Could not load the ranking");
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setSchools(Array.isArray(data?.schools) ? data.schools : []);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load the ranking");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const view = stageView(stages);
  const shown = useMemo(() => columnsFor(view), [view]);

  const councilOptions = useMemo(() => {
    const names = new Set();
    schools.forEach((school) => {
      if (school.area) names.add(school.area);
    });
    return [...names].sort((a, b) => a.localeCompare(b, "en"));
  }, [schools]);

  const directoryById = useMemo(() => {
    const map = new Map();
    (directory || []).forEach((school) => {
      if (school && school.id != null) map.set(Number(school.id), school);
    });
    return map;
  }, [directory]);

  const ranked = useMemo(() => {
    const hasHome = Number.isFinite(home?.lat) && Number.isFinite(home?.lon);
    const withDistance = withMiles(withStageRanks(schools), home).map((row) => {
      const full = directoryById.get(Number(row.id));
      const check = catchmentCheckBySchoolId?.[row.id] || catchmentCheckBySchoolId?.[Number(row.id)];
      const standing = catchmentStanding(full, check, row.miles, { ready: catchmentReady, hasHome });
      const order = { in: 1, open: 2, possible: 3, out: 4 };
      return {
        ...row,
        catchment: standing.label,
        catchmentKey: standing.key,
        catchmentSort: order[standing.key] ?? null,
        ofstedSort: ofstedSortValue(row.ofsted),
      };
    });
    return rankSchools(withDistance, {
      gender: genders,
      councils: councilsOn,
      catchment: catchments,
      query,
      sortKey: sort.key,
      sortDir: sort.dir,
    });
  }, [schools, home, directoryById, catchmentCheckBySchoolId, catchmentReady, genders, councilsOn, catchments, query, sort]);

  const gcseYear = yearLabel(
    schools.reduce((max, school) => Math.max(max, school.gcseYear || 0), 0) || null
  );
  const ks5Year = yearLabel(
    schools.reduce((max, school) => Math.max(max, school.ks5Year || 0), 0) || null
  );
  const destYear = yearLabel(
    schools.reduce((max, school) => Math.max(max, school.destYear || 0), 0) || null
  );
  const placeLabel = home?.postcode ? home.postcode : "your pin";
  const showGcse = view !== "alevel";
  const showAlevel = view !== "gcse";
  const sortColumn = shown.find((column) => column.key === sort.key);
  const sortLabel = sortColumn?.sortName
    || (sortColumn?.rankOf ? `${sortColumn.rankOf} rank` : sortColumn?.label)
    || "name";
  const note = [
    `${ranked.length} grammar school${ranked.length === 1 ? "" : "s"}, ranked by ${sortLabel}.`,
    showGcse && gcseYear ? `GCSE ${gcseYear}. Rank is by Attainment 8.` : "",
    showAlevel && ks5Year ? `A level ${ks5Year}. Rank is by points per entry.` : "",
    view === "both" && destYear ? `University is ${destYear} leavers.` : "",
    view === "both" ? "Oxbridge is the number of offers the school has published." : "",
    "Catchment is from the home postcode. Open shows the published number of open places.",
    view === "both" ? "Department for Education, except Oxbridge and Ofsted." : "Department for Education, except Ofsted.",
    "A blank cell was not published.",
    nearMe ? `Order is straight-line miles from ${placeLabel}.` : "",
  ].filter(Boolean).join(" ");

  function chooseStage(id) {
    const next = toggleChoice(stages, id);
    const nextView = stageView(next);
    setStages(next);
    if (!nearMe && nextView !== view) setSort(defaultSort(nextView));
  }

  function chooseMany(selected, setSelected, id) {
    setSelected(toggleChoice(selected, id));
  }

  function chooseNear() {
    setNearMe((on) => {
      const next = !on;
      setSort(next ? { key: "miles", dir: 1 } : defaultSort(view));
      return next;
    });
  }

  function chooseSort(key) {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir * -1 };
      const lowerFirst =
        key === "miles" ||
        key === "name" ||
        key === "gcseRank" ||
        key === "alevelRank" ||
        key === "catchmentSort" ||
        key === "ofstedSort";
      return { key, dir: lowerFirst ? 1 : -1 };
    });
    setNearMe(key === "miles");
  }

  return (
    <div className="rk-page">
      <div className="rk-filters">
        <input
          className="rk-find"
          type="search"
          value={query}
          placeholder="Find a school"
          aria-label="Find a school in the ranking"
          onChange={(e) => setQuery(e.target.value)}
        />
        <FilterGroup
          label="What to rank"
          options={STAGE_OPTIONS}
          selected={stages}
          onToggle={chooseStage}
        />
        <FilterGroup
          label="School"
          options={GENDER_OPTIONS}
          selected={genders}
          onToggle={(id) => chooseMany(genders, setGenders, id)}
        />
        <FilterGroup
          label="Catchment"
          options={CATCHMENT_OPTIONS}
          selected={catchments}
          onToggle={(id) => chooseMany(catchments, setCatchments, id)}
        />
        <CouncilSelect
          councils={councilOptions}
          selected={councilsOn}
          onChange={setCouncilsOn}
        />
        <button
          type="button"
          className={`rk-near${nearMe ? " is-on" : ""}`}
          aria-pressed={nearMe}
          onClick={chooseNear}
        >
          Closer to {placeLabel}
        </button>
      </div>

      {error ? <div className="rk-status rk-status--error" role="alert">{error}</div> : null}
      {loading ? <div className="rk-status">Loading ranking…</div> : null}

      {!loading && !error ? (
        <div className="rk-table-wrap">
          <table className="rk-table">
            <caption className="rk-caption">
              {view === "both" ? "GCSE and A level ranking" : view === "gcse" ? "GCSE ranking" : "A level ranking"}
              {genders.length ? `, ${genders.join(" and ").toLowerCase()} schools` : ""}
              {`, ${ranked.length} shown, ranked by ${sortLabel}`}
            </caption>
            <thead>
              <tr className="rk-group-row">
                <th className="rk-group-corner" colSpan={2} scope="colgroup" />
                {columnGroups(shown).map((group) => {
                  const splitLeft = groupSplitLeft(group.id, shown);
                  return (
                    <th
                      key={group.id}
                      className={`rk-group rk-group--${group.id}${splitLeft ? " rk-split-left" : ""}`}
                      colSpan={group.count}
                      scope="colgroup"
                    >
                      <span className="v2-info-tip--label">
                        {group.label}
                        {GROUP_TIPS[group.id] ? (
                          <InfoTip text={GROUP_TIPS[group.id]} label={`About ${group.label}`} />
                        ) : null}
                      </span>
                    </th>
                  );
                })}
              </tr>
              <tr className="rk-cols">
                <th className="rk-col-rank" scope="col">#</th>
                <th className="rk-col-school" scope="col">
                  <button type="button" className="rk-sort" onClick={() => chooseSort("name")}>
                    School
                    <SortMark active={sort.key === "name"} dir={sort.dir} />
                  </button>
                </th>
                {shown.map((column) => (
                  <th key={column.key} scope="col" className={bandClass(column, shown)}>
                    <button type="button" className="rk-sort rk-sort--num" onClick={() => chooseSort(column.key)}>
                      {column.label}
                      <SortMark active={sort.key === column.key} dir={sort.dir} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ranked.length === 0 ? (
                <tr>
                  <td className="rk-empty" colSpan={2 + shown.length}>
                    No school matches those filters.
                  </td>
                </tr>
              ) : (
                ranked.map((school) => (
                  <tr key={school.id}>
                    <td className={`rk-col-rank${school.rank <= 3 ? ` rk-rank rk-rank--${school.rank}` : ""}`}>
                      {school.rank}
                    </td>
                    <td className="rk-col-school">
                      <button
                        type="button"
                        className="rk-name"
                        onClick={() => onOpenSchool?.(school)}
                      >
                        {school.name}
                      </button>
                      <div className="rk-meta">
                        {[school.area, school.gender].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    {shown.map((column) => (
                      <td key={column.key} className={bandClass(column, shown)}>
                        {column.display
                          ? (school[column.display] || "—")
                          : formatRankValue(school[column.key], column)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
      <p className="rk-foot">{note}</p>
    </div>
  );
}
